import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Calendar, Store, Share2, Eye } from "lucide-react";
import { DecklistBlock } from "../components/news/DecklistBlock";
import { CATEGORY_STYLES, formatDate } from "./NewsPage";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const MarkdownBody = ({ children }) => (
  <div className="prose prose-invert max-w-none prose-headings:text-yellow-300 prose-a:text-pink-400 prose-strong:text-white prose-blockquote:border-pink-500 prose-blockquote:text-gray-300 prose-table:text-sm prose-th:text-yellow-300 prose-td:border-purple-900/50 prose-th:border-purple-900/50 prose-li:marker:text-pink-400 text-gray-200" data-testid="markdown-body">
    <ReactMarkdown>{children || ""}</ReactMarkdown>
  </div>
);

export default function NewsDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null); setError(false);
    axios.get(`${API}/news/${slug}`).then((r) => setPost(r.data)).catch(() => setError(true));
  }, [slug]);

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: post.titulo, url }).catch(() => {});
    else window.open(`https://wa.me/?text=${encodeURIComponent(post.titulo + " " + url)}`, "_blank");
  };

  if (error) return <div className="min-h-screen bg-black pt-32 text-center text-gray-400" data-testid="news-not-found">Publicación no encontrada. <Link to="/noticias" className="text-pink-400">Volver</Link></div>;
  if (!post) return <div className="min-h-screen bg-black pt-32 flex justify-center"><div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <article className="min-h-screen bg-black pt-20 pb-16" data-testid="news-detail-page">
      <div className="relative h-[42vh] min-h-[280px] overflow-hidden">
        {post.imagen_portada && <img src={post.imagen_portada} alt="" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        <div className="absolute inset-x-0 bottom-0 container mx-auto px-4 pb-8">
          <Link to="/noticias" className="inline-flex items-center gap-2 text-pink-400 text-sm mb-4 hover:text-pink-300"><ArrowLeft size={16} /> Noticias</Link>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className={`px-2 py-0.5 rounded text-xs font-bold border ${CATEGORY_STYLES[post.categoria] || CATEGORY_STYLES.Noticias}`}>{post.categoria}</span>
            <span className="px-2 py-0.5 rounded text-xs bg-purple-800/60 text-purple-200">{post.juego}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight max-w-4xl" data-testid="news-title">{post.titulo}</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 mt-8 grid lg:grid-cols-[1fr_300px] gap-10">
        <div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-8 pb-4 border-b border-purple-900/50">
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {formatDate(post.fecha)}</span>
            {post.autor && <span>Por <span className="text-white">{post.autor}</span></span>}
            <span className="flex items-center gap-1.5"><Eye size={14} /> {post.views || 0}</span>
            <button onClick={share} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-purple-700/60 hover:border-pink-500 text-gray-300 hover:text-white transition" data-testid="news-share-btn"><Share2 size={14} /> Compartir</button>
          </div>
          {post.resumen && <p className="text-lg text-gray-300 italic mb-6">{post.resumen}</p>}
          <MarkdownBody>{post.contenido_markdown}</MarkdownBody>
          <DecklistBlock decklist={post.decklist} decklistText={post.decklist_text} />
        </div>

        <aside className="space-y-6">
          {post.tienda && (
            <Link to="/tiendas" className="block glass-card rounded-2xl p-4 border border-purple-500/30 hover:border-yellow-400/50 transition" data-testid="news-store-card">
              <p className="text-[11px] tracking-widest text-gray-400 mb-2">TIENDA ASOCIADA</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-900/60 flex items-center justify-center text-yellow-400 overflow-hidden">{post.tienda.logo_url ? <img src={post.tienda.logo_url} alt="" className="w-full h-full object-cover" /> : <Store size={22} />}</div>
                <div><p className="font-bold text-white">{post.tienda.nombre}</p><p className="text-xs text-gray-400">{post.tienda.ciudad} · {post.tienda.estatus}</p></div>
              </div>
            </Link>
          )}
          {post.relacionados?.length > 0 && (
            <div className="glass-card rounded-2xl p-4 border border-purple-500/30">
              <p className="text-[11px] tracking-widest text-gray-400 mb-3">RELACIONADOS</p>
              <div className="space-y-3">
                {post.relacionados.map((r) => (
                  <Link key={r.id} to={`/noticias/${r.slug}`} className="flex gap-3 group">
                    <div className="w-16 h-12 rounded-lg overflow-hidden bg-purple-900/50 flex-shrink-0">{r.imagen_portada && <img src={r.imagen_portada} alt="" className="w-full h-full object-cover" />}</div>
                    <div><p className="text-sm text-white group-hover:text-yellow-300 leading-snug line-clamp-2">{r.titulo}</p><p className="text-[11px] text-gray-500">{formatDate(r.fecha)}</p></div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </article>
  );
}
